import { useSaveLocationMutation } from '@kinetic-ui/data-layer';
import {
  LocationFormToInput,
  LocationFormValues,
  useGraphLoading,
  useSaveHotKey,
  useLocationFormFields,
} from '@kinetic-ui/shared';
import { useLatest } from '@qfa/react-components';
import { Dispatch, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/* eslint-disable react-hooks/rules-of-hooks */
// we're technically returning a hook from a hook factory, which is against the rules of hooks,
// the reference is stable and doesn't violate the rules of hooks
type OnSaveComplete = Dispatch<{ id: string }>;

export const useCreateLocationForm = (onSaveComplete: OnSaveComplete) =>
  useCallback(
    (initialData: LocationFormValues, hasPrivilege: boolean) => {
      const { t } = useTranslation(['locations', 'common']);

      const {
        hasErrors,
        valuesProps: { values },
        saveButtonProps: { isSaveDisabled: isSaveButtonDisabled },
        valuesProps: { setValues },
      } = useLocationFormFields({ initialData, hasPrivilege });

      const isValidRef = useLatest(!hasErrors);

      const [saveLocation, result] = useSaveLocationMutation();
      useGraphLoading({
        result,
        name: 'saveLocation',
        messages: {
          success: t('savedLocationSuccess'),
          error: t('saveLocationError'),
        },
      });
      const valuesRef = useLatest(values);

      const onSaveClick = useCallback(() => {
        if (!isValidRef.current) {
          return;
        }
        const locationInput = LocationFormToInput(valuesRef?.current);

        return saveLocation({
          variables: {
            input: locationInput,
          },
        }).then(({ data }) => {
          if (data?.saveLocation) {
            // close tab and fire setBroadcastState
            onSaveComplete({ id: data.saveLocation.id });
          }
        });
      }, [isValidRef, saveLocation, valuesRef]);

      useSaveHotKey(isSaveButtonDisabled, onSaveClick);

      const returnData = useMemo(
        () => ({
          headerProps: {
            title: t('createLocation'),
          },
          valuesProps: {
            values,
            setValues,
          },
          hasErrors,
          saveButtonProps: {
            onSaveClick,
            isSaveDisabled: isSaveButtonDisabled,
          },
        }),
        [t, values, setValues, hasErrors, onSaveClick, isSaveButtonDisabled]
      );

      return returnData;
    },
    [onSaveComplete]
  );
